import { useEffect } from 'react';

import {
  useUIPreferences,
  useAccessibilitySettings,
  applyAccessibilitySettings,
  restoreAccessibilitySettings,
  useSettingsStore,
  applyTheme,
  getStoredThemePreference,
  resolveTheme,
  type Theme,
  type ResolvedTheme,
} from '@tankef/core-react';
import { tokens } from '@tankef/ui/tokens';

import { useClientIsAuthenticated, useCurrentPartyProfile } from '@/domains/identity/auth/api';
import { useVersionCheck } from '@/shared/hooks/useVersionCheck';

const lightThemeFallbackTokens: Record<string, string> = {
  background: tokens.colors.surface.white,
  surface: tokens.colors.surface.light,
  text: tokens.colors.text.primary,
  text_muted: tokens.colors.text.secondary,
  border: tokens.colors.border.default,
  primary: tokens.colors.brand.teal,
  success: tokens.colors.success[500],
  warning: tokens.colors.warning[500],
  error: tokens.colors.error[500],
  font_family: tokens.typography.fontFamily.sans.join(', '),
};

// Dark mode values mirror the .dark scope in packages/ui/src/styles/semantic-vars.css.
const darkThemeFallbackTokens: Record<string, string> = {
  background: '#1A2E32',
  surface: '#243B40',
  text: '#E0F0ED',
  text_muted: '#8FA8A2',
  border: '#3A5A60',
  primary: tokens.colors.brand.teal,
  success: tokens.colors.success[500],
  warning: tokens.colors.warning[500],
  error: tokens.colors.error[500],
  font_family: tokens.typography.fontFamily.sans.join(', '),
};

const crossThemeTokenKeys = ['primary', 'success', 'warning', 'error', 'font_family'] as const;

function pickCrossThemeTokens(designTokens: Record<string, string>): Record<string, string> {
  const safeTokens: Record<string, string> = {};

  for (const key of crossThemeTokenKeys) {
    const value = designTokens[key];
    if (value) {
      safeTokens[key] = value;
    }
  }

  return safeTokens;
}

function getThemeTokenMap(
  resolvedTheme: ResolvedTheme,
  backendTheme: Theme | undefined,
  designTokens: Record<string, string>
): Record<string, string> {
  const fallbackTokens =
    resolvedTheme === 'dark' ? darkThemeFallbackTokens : lightThemeFallbackTokens;
  const resolvedBackendTheme =
    backendTheme === 'light' || backendTheme === 'dark' ? backendTheme : resolvedTheme;

  return {
    ...fallbackTokens,
    ...(resolvedBackendTheme === resolvedTheme ? designTokens : pickCrossThemeTokens(designTokens)),
  };
}

function applyThemeTokens(tokensMap: Record<string, string>): void {
  const rootStyle = document.documentElement.style;

  for (const [key, value] of Object.entries(tokensMap)) {
    rootStyle.setProperty(`--${key.replace(/_/g, '-')}`, value);
  }
}

/**
 * AppInitializer - Applies user preferences on app load
 * Fetches and applies:
 * - Theme preference (light/dark/system)
 * - Language preference
 * - Compact mode
 * - Design tokens (colors)
 * - Accessibility settings (font scale, contrast, motion, screen reader)
 */
export function AppInitializer() {
  const isAuthenticated = useClientIsAuthenticated();
  const { data: uiPreferences } = useUIPreferences();
  const { data: accessibilitySettings } = useAccessibilitySettings();

  // Keep party store fresh after login — firstName/fullName used for greetings and initials
  useCurrentPartyProfile(isAuthenticated);
  const { display, setTheme, setLanguage, setCompactMode } = useSettingsStore();

  // Apply UI preferences (theme, language, compact mode, design tokens)
  useEffect(() => {
    if (!uiPreferences?.data) return;

    const html = document.documentElement;
    const {
      theme = 'system',
      locale = 'es',
      compact_mode: compactMode = false,
    } = uiPreferences.data.preferences || {};
    const designTokens = uiPreferences.data.design_tokens || {};
    const themePreference =
      getStoredThemePreference() ||
      (display.theme !== 'system' ? display.theme : undefined) ||
      theme;
    const resolvedTheme = resolveTheme(themePreference);

    if (display.theme !== themePreference) {
      setTheme(themePreference);
    }

    // Apply theme to DOM and runtime design tokens
    applyTheme(themePreference);
    applyThemeTokens(getThemeTokenMap(resolvedTheme, theme, designTokens));

    // Apply locale/language to store and DOM
    setLanguage(locale as 'es' | 'en');
    html.lang = locale;

    // Apply compact mode to store and DOM
    setCompactMode(compactMode);
    html.classList.toggle('compact-mode', compactMode);

    if (themePreference !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleThemeChange = () => {
      const nextResolvedTheme = resolveTheme('system');
      applyTheme('system');
      applyThemeTokens(getThemeTokenMap(nextResolvedTheme, theme, designTokens));
    };

    mediaQuery.addEventListener('change', handleThemeChange);

    return () => {
      mediaQuery.removeEventListener('change', handleThemeChange);
    };
  }, [uiPreferences, display.theme, setTheme, setLanguage, setCompactMode]);

  // Restore from localStorage immediately (before API resolves) so there's no
  // flash of un-scaled text or missing high-contrast on first paint.
  useEffect(() => {
    restoreAccessibilitySettings();
  }, []);

  // Sync from API when it loads — overwrites localStorage with the authoritative value
  // API shape: { success, data: { preferences: { font_scale, ... } } }
  useEffect(() => {
    const prefs = accessibilitySettings?.data?.data?.preferences;
    if (!prefs) return;
    applyAccessibilitySettings(prefs);
  }, [accessibilitySettings]);

  const { hasUpdate, reload } = useVersionCheck();

  if (!hasUpdate) return null;

  return (
    <div
      role="status"
      aria-live="polite"
      className="fixed bottom-0 left-0 right-0 z-banner flex items-center justify-between gap-4 bg-primary-600 px-4 py-3 text-white shadow-lg"
    >
      <span className="text-sm font-medium">
        Nueva versión disponible. Recarga para obtener las últimas mejoras.
      </span>
      <button
        type="button"
        onClick={reload}
        className="shrink-0 rounded-full border border-white/40 px-4 py-1.5 text-sm font-semibold transition-colors hover:bg-white/15"
      >
        Actualizar ahora
      </button>
    </div>
  );
}
